import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EllipsisVerticalIcon, LogoutIcon, PasskeyIcon } from '@/components/Icons';
import AvatarIcon from '@/components/AvatarIcon';
import { useProfile } from '@/hooks/useProfile';
import useAuth from '@/hooks/useAuth';
import useXero from '@/hooks/useXero';
import useHomeContext from '@/hooks/useHomeContext';
import { useClickOutside } from '@/hooks/useClickOutside';
import { colorVariant } from './colorVariant';
import cn from '@/utils/cn';
import { sideNav } from '@/utils/translations';
import { routes } from '@/utils/routes';

const NAME = cn(colorVariant({ text: 'white' }), 'text-xs font-medium leading-tight');

const ACCESS_LEVEL = cn(colorVariant({ text: 'navy-4' }), 'text-2xs font-medium leading-tight');

const CONTAINER = 'relative flex items-end justify-between px-4 py-3';

const USER_INFO_ROW = 'flex items-center gap-2';

const TEXT_STACK = 'flex flex-col';

// Plain vertical-dots icon now supplies no background of its own (see EllipsisVertical.tsx) -
// this wrapper is what actually renders the hover-highlight circle, matching
// team-members.html's `.who-more` (transparent by default, `rgba(255,255,255,.08)` + white text
// on hover).
const MENU_TOGGLE =
  'flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full text-white/70 hover:bg-white/10 hover:text-white transition-colors';

// w-52 (not the Figma's literal 224px) - this sidebar's expanded width is 208px, so matching
// that keeps the menu flush with the sidebar's own edges instead of overhanging it.
const MENU_CONTAINER =
  'absolute bottom-full right-2 mb-2 w-48 bg-dark-navy-10 rounded-lg shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.10),0px_2px_4px_-2px_rgba(0,0,0,0.05)] overflow-hidden flex flex-col';

const MENU_ITEM = 'flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-white/5';

const UserProfile = () => {
  const auth = useAuth()!;
  const xero = useXero()!;
  const homeContext = useHomeContext()!;
  const navigate = useNavigate();
  const { name, preferredName, accessLevel, photoURL } = useProfile();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useClickOutside(menuRef, () => setShowMenu(false));

  const handleLogout = () => {
    auth.onLogout();
    xero.clearXeroContext();
    homeContext.clearHomeContext();
  };

  const handleNavigateToPasskeys = () => {
    setShowMenu(false);
    navigate(routes.passkeySettings);
  };

  return (
    <div ref={menuRef} className={CONTAINER}>
      <div className={USER_INFO_ROW}>
        <AvatarIcon
          key="user-profile-avatar"
          name={preferredName ?? name}
          imgSrc={photoURL}
          variant="sm"
        />
        <div className={TEXT_STACK}>
          <span className={NAME}>{preferredName ?? name}</span>
          <span className={ACCESS_LEVEL}>{accessLevel}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setShowMenu((prev) => !prev)}
        className={MENU_TOGGLE}
        aria-label="User settings"
        aria-haspopup="menu"
        aria-expanded={showMenu}>
        <EllipsisVerticalIcon size="sm" />
      </button>
      {showMenu ? (
        <div className={MENU_CONTAINER} role="menu" aria-label="User settings">
          <button
            type="button"
            className={MENU_ITEM}
            onClick={handleNavigateToPasskeys}
            role="menuitem">
            <PasskeyIcon size="xs" className="brightness-0 invert" />
            <span className="text-white text-xs font-medium">{sideNav.passkeys}</span>
          </button>
          <div className="w-full h-px bg-white/20" />
          <button type="button" className={MENU_ITEM} onClick={handleLogout} role="menuitem">
            <LogoutIcon size="xs" className="text-error-darkred" />
            <span className="text-error-darkred text-xs font-medium">{sideNav.logout}</span>
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default UserProfile;
